import { useEffect, useState } from "react";
import { api, brl } from "../lib/api";
import { MarketplaceTag } from "../components/StatusBadge";
import { toast } from "sonner";
import { Loader2, Check, X, Edit3, Send, AlertOctagon, CheckCheck, Square, CheckSquare } from "lucide-react";

export default function Billing() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // { order, value, type }
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders?hub_status=awaiting_review");
      const { data: blocked } = await api.get("/orders?hub_status=blocked");
      setOrders([...data, ...blocked]);
    } catch (e) {
      toast.error("Erro ao carregar conferência");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (order, override) => {
    setBusy(order.id);
    try {
      const body = override
        ? { discount_override: override.value, discount_type_override: override.type, reason: "Editado na conferência" }
        : {};
      await api.post(`/orders/${order.id}/approve`, body);
      toast.success("Pedido aprovado para faturamento");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao aprovar");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (order) => {
    const reason = window.prompt("Motivo da rejeição?", "Pedido inconsistente");
    if (!reason) return;
    setBusy(order.id);
    try {
      await api.post(`/orders/${order.id}/reject`, { reason });
      toast.success("Pedido rejeitado");
      load();
    } catch (e) {
      toast.error("Falha ao rejeitar");
    } finally {
      setBusy(null);
    }
  };

  const sendToErp = async (order) => {
    setBusy(order.id);
    try {
      await api.post(`/orders/${order.id}/send-to-erp`);
      toast.success("Pedido enviado ao ERP — NF autorizada (simulado)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no envio");
    } finally {
      setBusy(null);
    }
  };

  // Bulk selection
  const selectable = orders.filter((o) => o.hub_status === "awaiting_review");
  const allSelected = selectable.length > 0 && selectable.every((o) => selected.has(o.id));
  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(selectable.map((o) => o.id)));
  const clearAll = () => setSelected(new Set());

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Aprovar ${selected.size} pedido(s)?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/orders/bulk-approve", { order_ids: Array.from(selected) });
      toast.success(`${data.approved_count} aprovado(s)${data.skipped.length ? `, ${data.skipped.length} ignorado(s)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na aprovação em lote");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="billing-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Faturamento</div>
          <h1 className="text-3xl font-black tracking-tighter">Aguardando Conferência</h1>
          <p className="text-sm text-slate-600 mt-1">
            Conferência manual obrigatória. Nenhum pedido é faturado automaticamente nesta versão.
          </p>
        </div>
        {selectable.length > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-toolbar">
            <button
              data-testid="billing-toggle-all"
              onClick={allSelected ? clearAll : selectAll}
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 flex items-center gap-1.5"
            >
              {allSelected ? <Square className="w-3.5 h-3.5" /> : <CheckSquare className="w-3.5 h-3.5" />}
              {allSelected ? "Desmarcar todos" : "Selecionar todos"}
            </button>
            <button
              data-testid="billing-bulk-approve"
              onClick={bulkApprove}
              disabled={selected.size === 0 || bulkBusy}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 disabled:opacity-50 disabled:hover:bg-emerald-600"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
              Aprovar selecionados ({selected.size})
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-slate-200 bg-white py-16 text-center text-sm text-slate-500">
          Nenhum pedido aguardando conferência. 🎯
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isBlocked = o.hub_status === "blocked";
            const editing = edit?.order?.id === o.id;
            return (
              <div
                key={o.id}
                className={`border bg-white ${isBlocked ? "border-red-300" : "border-slate-200"} ${
                  selected.has(o.id) ? "ring-2 ring-[#002EA6]" : ""
                }`}
                data-testid={`billing-card-${o.order_number}`}
              >
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {!isBlocked && o.hub_status === "awaiting_review" && (
                      <input
                        type="checkbox"
                        data-testid={`billing-check-${o.order_number}`}
                        checked={selected.has(o.id)}
                        onChange={() => toggleOne(o.id)}
                        className="w-4 h-4 accent-[#002EA6] cursor-pointer"
                      />
                    )}
                    <MarketplaceTag id={o.marketplace} />
                    <div className="font-mono text-sm font-bold">{o.order_number}</div>
                    <div className="text-xs text-slate-500">{o.company_name}</div>
                  </div>
                  {isBlocked && (
                    <div className="flex items-center gap-2 px-2 py-1 bg-red-50 border border-red-200 text-red-800 text-[10px] font-bold uppercase tracking-wider">
                      <AlertOctagon className="w-3.5 h-3.5" />
                      Bloqueado · {o.marketplace_status}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 py-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
                      Cliente
                    </div>
                    <div className="text-sm font-semibold">{o.customer_name}</div>
                    <div className="font-mono text-xs text-slate-500">{o.customer_doc}</div>
                    <div className="text-xs text-slate-600 mt-1">{o.shipping_address}</div>
                    <div className="font-mono text-xs text-slate-500">CEP {o.shipping_zip}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
                      Itens
                    </div>
                    <table className="w-full text-sm border-t border-slate-100">
                      <tbody>
                        {o.items.map((it, idx) => (
                          <tr key={idx} className="border-b border-slate-50">
                            <td className="py-1 pr-3 font-mono text-xs font-bold">{it.sku}</td>
                            <td className="py-1 pr-3 text-xs">{it.product_name}</td>
                            <td className="py-1 pr-3 text-xs font-mono text-right">{it.quantity}x</td>
                            <td className="py-1 text-xs font-mono text-right">
                              {brl(it.unit_price * it.quantity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Valor original</div>
                    <div className="font-mono text-sm">{brl(o.value_original)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Desconto</div>
                    {editing ? (
                      <div className="flex gap-1">
                        <input
                          data-testid={`billing-edit-value-${o.order_number}`}
                          type="number"
                          step="0.01"
                          value={edit.value}
                          onChange={(e) => setEdit({ ...edit, value: parseFloat(e.target.value) || 0 })}
                          className="w-20 border border-slate-300 px-1 py-1 text-sm"
                        />
                        <select
                          data-testid={`billing-edit-type-${o.order_number}`}
                          value={edit.type}
                          onChange={(e) => setEdit({ ...edit, type: e.target.value })}
                          className="border border-slate-300 px-1 py-1 text-xs"
                        >
                          <option value="percent">%</option>
                          <option value="fixed">R$</option>
                        </select>
                      </div>
                    ) : (
                      <div className="font-mono text-sm text-amber-700">- {brl(o.value_discount)}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Valor final</div>
                    <div className="font-mono text-base font-bold">{brl(o.value_final)}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    {!isBlocked && !editing && (
                      <>
                        <button
                          data-testid={`billing-edit-${o.order_number}`}
                          onClick={() => setEdit({ order: o, value: o.value_discount, type: "fixed" })}
                          className="px-2.5 py-1.5 text-xs border border-slate-300 hover:border-slate-900 flex items-center gap-1"
                        >
                          <Edit3 className="w-3.5 h-3.5" /> Editar desconto
                        </button>
                        <button
                          data-testid={`billing-reject-${o.order_number}`}
                          onClick={() => reject(o)}
                          disabled={busy === o.id}
                          className="px-2.5 py-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"
                        >
                          <X className="w-3.5 h-3.5" /> Rejeitar
                        </button>
                        <button
                          data-testid={`billing-approve-${o.order_number}`}
                          onClick={() => approve(o)}
                          disabled={busy === o.id}
                          className="px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                        >
                          {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Aprovar
                        </button>
                      </>
                    )}
                    {!isBlocked && editing && (
                      <>
                        <button
                          onClick={() => setEdit(null)}
                          className="px-2.5 py-1.5 text-xs border border-slate-300 hover:border-slate-900"
                        >
                          Cancelar
                        </button>
                        <button
                          data-testid={`billing-approve-edited-${o.order_number}`}
                          onClick={() => approve(edit.order, { value: edit.value, type: edit.type })}
                          className="px-3 py-1.5 text-xs font-bold bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-1"
                        >
                          <Send className="w-3.5 h-3.5" /> Aprovar com desconto
                        </button>
                      </>
                    )}
                    {o.hub_status === "approved" && (
                      <button
                        data-testid={`billing-send-erp-${o.order_number}`}
                        onClick={() => sendToErp(o)}
                        className="px-3 py-1.5 text-xs font-bold bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-1"
                      >
                        <Send className="w-3.5 h-3.5" /> Enviar ao ERP
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ApprovedSection onSendErp={sendToErp} busy={busy} />
    </div>
  );
}

function ApprovedSection({ onSendErp, busy }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await api.get("/orders?hub_status=approved");
    setItems(data);
  };
  useEffect(() => { load(); }, []);
  if (items.length === 0) return null;
  return (
    <div className="border border-emerald-200 bg-emerald-50/40">
      <div className="px-4 py-2.5 border-b border-emerald-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">
            Aprovados — aguardando envio ao ERP
          </div>
        </div>
        <button onClick={load} className="text-xs font-bold uppercase tracking-wider text-emerald-800 hover:underline">
          Atualizar
        </button>
      </div>
      <div className="divide-y divide-emerald-100">
        {items.map((o) => (
          <div key={o.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-3">
              <MarketplaceTag id={o.marketplace} />
              <div className="font-mono text-xs font-bold">{o.order_number}</div>
              <div className="text-xs text-slate-600">{o.customer_name}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="font-mono text-sm font-bold">{brl(o.value_final)}</div>
              <button
                data-testid={`approved-send-erp-${o.order_number}`}
                onClick={() => onSendErp(o).then(load)}
                disabled={busy === o.id}
                className="px-3 py-1.5 text-xs font-bold bg-[#002EA6] hover:bg-[#00207A] text-white"
              >
                Enviar ao ERP
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
